import { SingleSelect } from "@learningblocks/simulations/shared-components/lib/Questions/SingleSelect";
import React, { useState } from "react";
import { WhatIsLightSpectrophotometryImage } from "../../SpectrophotometerImages";
import { Utilities } from "@learningblocks/exp-constants";
import cname from "../../style.module.scss";
import { useDispatch } from "react-redux";
import { whatIsLightAnsweredAction } from "../../store";
import WhatIsLightAnimation from "../../animations/what-is-light-animation.json";
import { SliderImage } from "@learningblocks/storybook-components/lib/SliderImage";

const { QUESTION_CONTENTS } = Utilities;

const staticContent = {
  [QUESTION_CONTENTS.TEXT]: {
    text: `<p>Most spectrophotometers can measure the full absorbance spectrum of a substance through a range of wavelengths (typically from 190-900 nm). In the interactive above, the spectrum for substance Z was measured between 350 and 700 nm.</p>
      <p>While the spectrophotometer can determine the amount of a
    substance in solution by measuring its absorbance, it is not necessary to
    read the entire spectrum. It is sufficient to choose the wavelength that is
    most readily absorbed by the substance of interest to obtain the most
    accurate measurements.</p>`,
  },
  [QUESTION_CONTENTS.QUESTION]: {
    text: "<h3>Which wavelength would you choose to measure the absorbance of substance Z?</h3>",
  },
};
export const Content = () => {
  const [options, setOptions] = useState([
    {
      id: 1,
      text: "400 nm",
      disabled: false,
      selected: false,
      value: 400,
      feedback:
        "At 400 nm, the absorbance for substance Z is 0.050. Is this the highest absorbance reported along the spectrum?",
    },
    {
      id: 2,
      text: "650 nm",
      disabled: false,
      selected: false,
      value: 650,
      feedback:
        "You found the wavelength at which substance Z absorbs the most light. The spectrophotometer should therefore be set to 650 nm to determine the concentration of the substance.",
    },
    {
      id: 3,
      text: "700 nm",
      disabled: false,
      selected: false,
      value: 700,
      feedback:
        "At 700 nm, the absorbance for substance Z is 0.300. Is this the highest absorbance reported along the spectrum?",
    },
  ]);
  const correctId = 2;
  const [completed, setCompleted] = useState(false);
  const [attempts, setAttempts] = useState(2);
  const [showFeedback, setShowFeedback] = useState(false);
  const [sliderValue, setSliderValue] = useState(350);
  const dispatch = useDispatch();

  const onSelect = ({ optionID }: { optionID: number }) => {
    setOptions(
      options.map((o) => ({
        ...o,
        selected: o.id === optionID,
      }))
    );
  };
  const onAnswer = () => {
    const currentSelected = options.find((o) => o.selected);
    if (
      (currentSelected && currentSelected.id === correctId) ||
      attempts - 1 === 0
    ) {
      setOptions(options.map((o) => ({ ...o, disabled: true })));
      setCompleted(true);
      dispatch(whatIsLightAnsweredAction());
      setAttempts(0);
    } else {
      setAttempts(attempts - 1);
      setOptions(options.map((o) => ({ ...o, disabled: o.selected })));
    }
  };
  const content = {
    ...staticContent,
    [QUESTION_CONTENTS.OPTIONS]: options,
  };
  return (
    <div className={cname.content}>
      <p>
        Since the spectrophotometer uses a beam of light to measure the
        absorbance of a solution, it is worth having a look at the nature of
        light.
      </p>
      <div className={cname.content_image_container}>
        <div className={cname.content_image}>
          <WhatIsLightSpectrophotometryImage />
        </div>
        <div className={cname.caption}>
          Figure 2: The spectrum of visible light is between the wavelengths of
          380 and 750 nanometers (nm). A mixture of all the wavelengths of
          visible light appears white to the eye. A prism can sort white light
          into its component colors by bending light of different wavelengths at
          different angles.
        </div>
      </div>
      <div>
        <p>
          Light is a form of energy that is also called electromagnetic
          radiation. The entire range of radiation is known as the
          electromagnetic spectrum. Visible light makes up only a small fraction
          of the spectrum and ranges from 380 nm to 750 nm in wavelength (Figure
          2).
        </p>
        <p>
          When white light hits matter, such as a chemical substance, some of
          the light might get absorbed. Different substances (or molecules) will
          absorb light of different wavelengths. In the spectrum below, we can
          see that substance Z absorbs some wavelengths of light more readily
          than others. It is important that the concentration of the sample is
          not too high since this would result in a bad quality spectrum in
          which peaks would be less visible. Use the slider below to determine
          the wavelength at which substance Z absorbs the most light.
        </p>
      </div>
      <div className={cname.slider_animation}>
        <SliderImage
          headTitle={""}
          ariaLabel="A line graph. The x-axis (labeled Wavelength) runs from 350 to 700 nanometers. The y-axis (labeled Absorbance) runs from below 0.0 to 1.0. A line on the graph shows the absorbance for “Substance Z.” Adjust the slider with the arrow keys to increase the wavelength."
          src={{
            isPath: false,
            wide: WhatIsLightAnimation,
            square: WhatIsLightAnimation,
          }}
          sliderValue={{
            min: 350,
            max: 700,
            step: 50,
            current: sliderValue,
            scaleInterval: 50,
            text: "Drag the slider to the right to increase the wavelength (in nanometers).",
          }}
          currentFrame={(sliderValue - 350) / 50}
          onSliderChange={(value) => setSliderValue(value)}
        />
      </div>

      <SingleSelect
        complete={completed}
        content={Object.keys(content)}
        answerId={[2]}
        totalAttempts={attempts}
        submitAnswer={onAnswer}
        selectOption={onSelect}
        onShowFeedback={() => setShowFeedback(true)}
        showFeedback={showFeedback}
        resetAnswer={() => {}}
        {...content}
      />
    </div>
  );
};
